import { Fragment, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Code,
  Collapse,
  Container,
  Group,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  UnstyledButton,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconArrowDown,
  IconArrowsSort,
  IconArrowUp,
  IconChevronDown,
  IconChevronRight,
  IconFilter,
  IconRefresh,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { ApiError, api } from "../api/client";
import type { AuditFilters, AuditLogEntry, AuditOptions } from "../api/types";
import { ResponsiveTable } from "../components/responsiveTable/ResponsiveTable";

const PAGE_SIZE = 50;

export function AuditLogPage() {
  const { t } = useTranslation();
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState<AuditOptions>({ actions: [], entity_types: [] });
  const [filterAction, setFilterAction] = useState<string | null>(null);
  const [filterEntity, setFilterEntity] = useState<string | null>(null);
  const [filterUser, setFilterUser] = useState("");
  const [filterSince, setFilterSince] = useState("");
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  type AuditSortKey = "created_at" | "username" | "action" | "entity_type" | "entity_id";
  type SortDir = "asc" | "desc";
  const [sortKey, setSortKey] = useState<AuditSortKey>("created_at");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  function toggleSort(key: AuditSortKey) {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir(key === "created_at" ? "desc" : "asc");
    }
  }

  const displayedLogs = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    const sortable = [...logs];
    sortable.sort((a, b) => {
      let av: string | number = "";
      let bv: string | number = "";
      switch (sortKey) {
        case "created_at": av = a.created_at; bv = b.created_at; break;
        case "username": av = a.username ?? ""; bv = b.username ?? ""; break;
        case "action": av = a.action; bv = b.action; break;
        case "entity_type": av = a.entity_type ?? ""; bv = b.entity_type ?? ""; break;
        case "entity_id": av = a.entity_id ?? -Infinity; bv = b.entity_id ?? -Infinity; break;
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return sortable;
  }, [logs, sortKey, sortDir]);

  function AuditSortHeader({ k, label }: { k: AuditSortKey; label: string }) {
    const isActive = sortKey === k;
    const Icon = isActive ? (sortDir === "asc" ? IconArrowUp : IconArrowDown) : IconArrowsSort;
    return (
      <UnstyledButton
        onClick={() => toggleSort(k)}
        style={{ display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer", fontWeight: 600 }}
      >
        {label}
        <Icon size={12} style={{ opacity: isActive ? 1 : 0.4 }} />
      </UnstyledButton>
    );
  }

  async function load() {
    setLoading(true);
    try {
      const filters: AuditFilters = {
        action: filterAction || undefined,
        entity_type: filterEntity || undefined,
        user_id: filterUser.trim() || undefined,
        since: filterSince || undefined,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      };
      const data = await api.listAuditLogs(filters);
      setLogs(data.logs);
      setTotal(data.total);
    } catch (err) {
      const detail = err instanceof ApiError ? JSON.stringify(err.detail) : String(err);
      notifications.show({
        color: "red",
        title: t("audit.loadFailedTitle"),
        message: detail.slice(0, 200),
      });
    } finally {
      setLoading(false);
    }
  }

  async function loadOptions() {
    try {
      setOptions(await api.auditOptions());
    } catch {
      // ignore — page still usable without filter dropdowns
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filterAction, filterEntity]);

  useEffect(() => {
    void loadOptions();
  }, []);

  function toggleExpanded(id: number) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function applyFilters() {
    setPage(1);
    void load();
  }

  function resetFilters() {
    setFilterAction(null);
    setFilterEntity(null);
    setFilterUser("");
    setFilterSince("");
    setPage(1);
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Container size="xl">
      <Group justify="space-between" mb="md">
        <Title order={2}>{t("audit.title")}</Title>
        <Button
          variant="subtle"
          leftSection={<IconRefresh size={14} />}
          onClick={() => void load()}
          loading={loading}
        >
          {t("audit.reload")}
        </Button>
      </Group>

      <Paper withBorder p="md" mb="md">
        <Group align="flex-end" wrap="wrap">
          <Select
            label={t("audit.actionLabel")}
            placeholder={t("common.all")}
            data={options.actions.map((a) => ({ value: a, label: a }))}
            value={filterAction}
            onChange={setFilterAction}
            clearable
            searchable
            w={240}
          />
          <Select
            label={t("audit.entityLabel")}
            placeholder={t("common.all")}
            data={options.entity_types.map((e) => ({ value: e, label: e }))}
            value={filterEntity}
            onChange={setFilterEntity}
            clearable
            w={160}
          />
          <TextInput
            label="User ID"
            placeholder={t("audit.userIdPlaceholder")}
            value={filterUser}
            onChange={(e) => setFilterUser(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") applyFilters();
            }}
            w={120}
          />
          <TextInput
            label={t("audit.sinceLabel")}
            placeholder="2026-05-01"
            value={filterSince}
            onChange={(e) => setFilterSince(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") applyFilters();
            }}
            w={180}
          />
          <Button leftSection={<IconFilter size={14} />} onClick={applyFilters}>
            {t("audit.filter")}
          </Button>
          <Button variant="subtle" onClick={resetFilters}>
            {t("audit.reset")}
          </Button>
          <Text size="sm" c="dimmed" ml="auto">
            {t("audit.recordCount", { count: total })}
          </Text>
        </Group>
      </Paper>

      <Paper withBorder>
        <ResponsiveTable
          mobile={{
            rows: displayedLogs,
            sort: {
              options: [
                { value: "created_at", label: t("audit.colTime") },
                { value: "username", label: "User" },
                { value: "action", label: t("audit.colAction") },
                { value: "entity_type", label: t("audit.colEntity") },
              ],
              value: sortKey,
              dir: sortDir,
              onChange: (v, d) => (v === sortKey ? setSortDir(d) : toggleSort(v as AuditSortKey)),
            },
            rowKey: (r) => r.id,
            title: (r) => (r.entity_type ? `${r.entity_type}${r.entity_id ? ` #${r.entity_id}` : ""}` : r.action),
            subtitle: (r) => `${new Date(r.created_at).toLocaleString()} · ${r.username ?? "(system)"}`,
            badge: (r) => (
              <Badge size="sm" variant="light" color={actionColor(r.action)}>
                {r.action}
              </Badge>
            ),
            fields: [
              {
                label: "Payload",
                render: (r) => <Code block fz="xs">{JSON.stringify(r.payload, null, 2)}</Code>,
                hidden: (r) => r.payload == null,
              },
              { label: "IP", render: (r) => r.ip_address, hidden: (r) => !r.ip_address },
            ],
            loading: loading && logs.length === 0,
            empty: t("audit.noRecords"),
          }}
        >
        <Table.ScrollContainer minWidth={720}>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={32} />
              <Table.Th w={170}><AuditSortHeader k="created_at" label={t("audit.colTime")} /></Table.Th>
              <Table.Th w={120}><AuditSortHeader k="username" label="User" /></Table.Th>
              <Table.Th miw={200} style={{ whiteSpace: "nowrap" }}>
                <AuditSortHeader k="action" label={t("audit.colAction")} />
              </Table.Th>
              <Table.Th w={140}><AuditSortHeader k="entity_type" label={t("audit.colEntity")} /></Table.Th>
              <Table.Th>Payload</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {loading && (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  <Text c="dimmed" size="sm">
                    {t("common.loading")}
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
            {!loading && logs.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  <Text c="dimmed" size="sm">
                    {t("audit.noRecords")}
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
            {displayedLogs.map((row) => {
              const isOpen = expanded.has(row.id);
              return (
                <Fragment key={row.id}>
                  <Table.Tr
                    style={{ cursor: "pointer" }}
                    onClick={() => toggleExpanded(row.id)}
                  >
                    <Table.Td>
                      {isOpen ? (
                        <IconChevronDown size={14} />
                      ) : (
                        <IconChevronRight size={14} />
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" ff="monospace">
                        {new Date(row.created_at).toLocaleString()}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {row.username ? (
                        <Text size="sm">{row.username}</Text>
                      ) : (
                        <Text size="sm" c="dimmed">
                          (system)
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td style={{ whiteSpace: "nowrap" }}>
                      <Badge size="sm" variant="light" color={actionColor(row.action)}>
                        {row.action}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      {row.entity_type ? (
                        <Text size="sm">
                          {row.entity_type}
                          {row.entity_id ? ` #${row.entity_id}` : ""}
                        </Text>
                      ) : (
                        <Text size="sm" c="dimmed">
                          —
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed" lineClamp={1}>
                        {row.payload ? JSON.stringify(row.payload) : "—"}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td colSpan={6} p={0}>
                      <Collapse in={isOpen}>
                        <Stack gap={4} p="sm">
                          {row.payload != null && (
                            <Code block fz="xs">
                              {JSON.stringify(row.payload, null, 2)}
                            </Code>
                          )}
                          {row.ip_address && (
                            <Text size="xs" c="dimmed">
                              IP: {row.ip_address}
                            </Text>
                          )}
                        </Stack>
                      </Collapse>
                    </Table.Td>
                  </Table.Tr>
                </Fragment>
              );
            })}
          </Table.Tbody>
        </Table>
        </Table.ScrollContainer>
        </ResponsiveTable>
      </Paper>

      {totalPages > 1 && (
        <Group justify="center" mt="md">
          <Pagination total={totalPages} value={page} onChange={setPage} siblings={1} />
        </Group>
      )}
    </Container>
  );
}

function actionColor(action: string): string {
  if (action.startsWith("device.flash") || action.startsWith("firmware")) return "violet";
  if (action.startsWith("godex")) return "blue";
  if (action.startsWith("shipment")) return "teal";
  if (action.startsWith("batch")) return "indigo";
  if (action.startsWith("documents")) return "grape";
  if (action.startsWith("device")) return "cyan";
  return "gray";
}
